import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import {
  ConversationMessage,
  MessageType,
  UserMessageContent,
} from "@stratify/shared";

/**
 * Service for interacting with agent conversations in Firestore
 */
export class AgentFirestoreService {
  /**
   * Adds a new user message to a conversation
   * @param pathPrefix The path prefix for the agent's conversations (e.g., 'agent_conversations')
   * @param conversationId The ID of the conversation
   * @param text The text content of the user's message
   */
  async addUserMessage(
    pathPrefix: string,
    conversationId: string,
    text: string
  ): Promise<void> {
    const messagesCollection = collection(
      db,
      `${pathPrefix}/${conversationId}/messages`
    );

    const messageData: Omit<ConversationMessage, "timestamp"> & {
      timestamp: any; // Using 'any' because serverTimestamp() type is complex
    } = {
      type: "user" as MessageType,
      content: {
        text,
      } as UserMessageContent,
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(messagesCollection, messageData);
    } catch (error) {
      console.error("Error adding user message:", error);
      throw new Error("Failed to add user message to conversation");
    }
  }

  /**
   * Subscribes to messages in a conversation
   * @param pathPrefix The path prefix for the agent's conversations
   * @param conversationId The ID of the conversation
   * @param callback Function to call with updated messages
   * @returns Unsubscribe function
   */
  subscribeToMessages(
    pathPrefix: string,
    conversationId: string,
    callback: (messages: ConversationMessage[]) => void
  ): () => void {
    const messagesCollection = collection(
      db,
      `${pathPrefix}/${conversationId}/messages`
    );
    const messagesQuery = query(
      messagesCollection,
      orderBy("timestamp", "asc")
    );

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messages: ConversationMessage[] = snapshot.docs.map((doc) =>
          this.convertMessage(doc)
        );
        callback(messages);
      },
      (error) => {
        console.error("Error in messages subscription:", error);
        // Instead of throwing (which would break the subscription),
        // we pass an error flag to the callback
        callback([
          {
            type: "error",
            timestamp: new Date().toISOString(),
            content: {
              message: "Failed to subscribe to messages",
              stack: error.stack,
            },
          } as ConversationMessage,
        ]);
      }
    );
  }

  /**
   * Converts a Firestore document to a ConversationMessage
   */
  private convertMessage(
    doc: DocumentSnapshot | QueryDocumentSnapshot
  ): ConversationMessage {
    const data = doc.data();
    if (!data) {
      throw new Error(`No data found for message document ${doc.id}`);
    }

    return {
      ...data,
      // Convert Firestore Timestamp to ISO string
      timestamp:
        data.timestamp?.toDate?.()?.toISOString() ?? new Date().toISOString(),
    } as ConversationMessage;
  }
}

// Export a singleton instance
export const agentFirestoreService = new AgentFirestoreService();
